'use client';

import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Lock, CheckCircle, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

function RegisterContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { signUp } = useAuth();
  const supabase = createClient();

  const inviteToken = searchParams.get('invite');

  const [validating, setValidating] = useState(!!inviteToken);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteId, setInviteId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;

    const validateToken = async () => {
      setValidating(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('invite_links')
          .select('id, email, expires_at, used_by')
          .eq('token', inviteToken)
          .single();

        if (fetchError || !data) {
          setInviteValid(false);
          return;
        }

        if (data.used_by) {
          setInviteValid(false);
          return;
        }

        if (new Date(data.expires_at) < new Date()) {
          setInviteValid(false);
          return;
        }

        setInviteValid(true);
        setInviteId(data.id);
        if (data.email) {
          setInviteEmail(data.email);
          setEmail(data.email);
        }
      } catch {
        setInviteValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [inviteToken, supabase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await signUp(email, password, { fullName });
      const userId = data?.user?.id;

      if (userId && inviteId) {
        await supabase
          .from('invite_links')
          .update({ used_by: userId, used_at: new Date().toISOString() })
          .eq('id', inviteId);
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // No invite token — show disabled message
  if (!inviteToken) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center gap-3 mb-2">
              <AppLogo size={40} />
              <span className="text-2xl font-bold text-[hsl(var(--foreground))] tracking-tight">HomeFinance</span>
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Rental Portfolio Tracker</p>
          </div>
          <div className="bg-white border border-[hsl(var(--border))] rounded-2xl shadow-sm p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center">
                <Lock size={26} className="text-[hsl(var(--muted-foreground))]" />
              </div>
            </div>
            <h1 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-2">Registration Disabled</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
              New account creation is currently not available. Please contact the administrator to request access.
            </p>
          </div>
          <p className="mt-6 text-center text-sm text-[hsl(var(--muted-foreground))]">
            Already have an account?{' '}
            <Link href="/login" className="text-[hsl(var(--primary))] font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Validating token
  if (validating) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw size={24} className="animate-spin text-[hsl(var(--muted-foreground))]" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Validating invite link…</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (!inviteValid) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center gap-3 mb-2">
              <AppLogo size={40} />
              <span className="text-2xl font-bold text-[hsl(var(--foreground))] tracking-tight">HomeFinance</span>
            </div>
          </div>
          <div className="bg-white border border-[hsl(var(--border))] rounded-2xl shadow-sm p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                <Lock size={26} className="text-red-400" />
              </div>
            </div>
            <h1 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-2">Invalid Invite Link</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
              This invite link is invalid, has already been used, or has expired. Please request a new invite from the administrator.
            </p>
          </div>
          <p className="mt-6 text-center text-sm text-[hsl(var(--muted-foreground))]">
            Already have an account?{' '}
            <Link href="/login" className="text-[hsl(var(--primary))] font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Success
  if (success) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle size={32} className="text-emerald-500" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-2">Account Created!</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
            Check your email to confirm your account, then sign in.
          </p>
          <Link
            href="/login"
            className="inline-flex items-center px-5 py-2.5 bg-[hsl(var(--primary))] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  // Registration form
  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center gap-3 mb-2">
            <AppLogo size={40} />
            <span className="text-2xl font-bold text-[hsl(var(--foreground))] tracking-tight">HomeFinance</span>
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Create your account</p>
        </div>

        <div className="bg-white border border-[hsl(var(--border))] rounded-2xl shadow-sm p-8">
          <div className="flex items-center gap-2 mb-6 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
            <CheckCircle size={15} className="text-emerald-600 shrink-0" />
            <p className="text-xs text-emerald-700 font-medium">Valid invite link — you can create an account below.</p>
          </div>

          {error && (
            <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1.5">Full Name</label>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
                className="w-full px-3 py-2.5 text-sm border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                readOnly={!!inviteEmail}
                placeholder="you@example.com"
                className={`w-full px-3 py-2.5 text-sm border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 ${inviteEmail ? 'opacity-70 cursor-not-allowed' : ''}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  className="w-full px-3 py-2.5 pr-10 text-sm border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[hsl(var(--primary))] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-60 transition-opacity flex items-center justify-center gap-2 mt-2"
            >
              {loading && <RefreshCw size={14} className="animate-spin" />}
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-[hsl(var(--muted-foreground))]">
          Already have an account?{' '}
          <Link href="/login" className="text-[hsl(var(--primary))] font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <RefreshCw size={24} className="animate-spin text-[hsl(var(--muted-foreground))]" />
      </div>
    }>
      <RegisterContent />
    </Suspense>
  );
}
